/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable max-len */

import {
	afterNextRender,
	ChangeDetectorRef,
	Component,
	Injector,
	Input,
	OnDestroy,
	OnInit
} from '@angular/core';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	RuleWorkflowDisplayDirective
} from '@shared/directives/rule-workflow-display.directive';
import {
	RuleListComponent
} from '@shared/dynamic-components/rules/rule-list/rule-list.component';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	StringHelper
} from '@shared/helpers/string.helper';
import {
	EntityInstanceRuleViolation
} from '@shared/implementations/entities/entity-instance-rule-violation';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IRuleDefinition
} from '@shared/interfaces/rules/rule-definition.interface';
import {
	IRulePresentationDefinition
} from '@shared/interfaces/rules/rule-presentation-definition.interface';
import {
	IRuleViolationWorkflowActionDefinition
} from '@shared/interfaces/rules/rule-violation-workflow-action-definition.interface';
import {
	NavigationViolationService
} from '@shared/services/navigation-violation.service';
import {
	RuleService
} from '@shared/services/rule.service';
import {
	Subscription
} from 'rxjs';

/* eslint-enable max-len */

@Component({
	selector: 'app-rule-list-item',
	templateUrl: './rule-list-item.component.html',
	styleUrls: [
		'./rule-list-item.component.scss'
	]
})

/**
 * A component representing a context level rule list item view.
 *
 * @export
 * @class RuleListItemComponent
 * @extends {RuleWorkflowDisplayDirective}
 * @implements {OnDestroy}
 * @implements {OnInit}
 * @implements {IDynamicComponent<Component, any>}
 */
export class RuleListItemComponent
	extends RuleWorkflowDisplayDirective
	implements OnDestroy, OnInit, IDynamicComponent<Component, any>
{
	/**
	 * Initializes a new instance of the rule list item component.
	 *
	 * @param {RuleService} ruleService
	 * The rule service used in this component.
	 * @param {Injector} injector
	 * The injector used for afterNextRender callbacks.
	 * @param {ChangeDetectorRef} changeDetectorRef
	 * The change detector reference for this component.
	 * @param {NavigationViolationService} navigationViolationService
	 * The navigation violation service used to track label updates.
	 * @memberof RuleListItemComponent
	 */
	public constructor(
		public ruleService: RuleService,
		private readonly injector: Injector,
		private readonly changeDetectorRef: ChangeDetectorRef,
		private readonly navigationViolationService:
			NavigationViolationService)
	{
		super();
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<Component, any>}
	 * @memberof RuleListItemComponent
	 */
	@Input() public context: IDynamicComponentContext<Component, any>;

	/**
	 * Gets or sets the rule definition for this violation list item.
	 *
	 * @type {IRuleDefinition}
	 * @memberof RuleListItemComponent
	 */
	public ruleDefinition: IRuleDefinition;

	/**
	 * Gets or sets the unique rule presentation definitions for this violation
	 * list item. This set only holds one presentation definition value for each
	 * associated data key in order to show associated controls.
	 *
	 * @type {IRulePresentationDefinition[]}
	 * @memberof RuleListItemComponent
	 */
	public rulePresentationDefinitions:
		IRulePresentationDefinition[] = [];

	/**
	 * Gets or sets the loading value of tables displayed in this list item.
	 *
	 * @type {boolean}
	 * @memberof RuleListItemComponent
	 */
	public loadingTables: boolean = true;

	/**
	 * Gets or sets the array item label for repeater context display.
	 *
	 * @type {string}
	 * @memberof RuleListItemComponent
	 */
	public arrayItemLabel: string = AppConstants.empty;

	/**
	 * Gets whether the array item label has a value.
	 *
	 * @type {boolean}
	 * @memberof RuleListItemComponent
	 */
	public get hasArrayItemLabel(): boolean
	{
		return !AnyHelper.isNullOrEmpty(this.arrayItemLabel);
	}

	/**
	 * Gets or sets the set of pending timeout handles that are scheduled
	 * for execution. This is used to cancel any pending timeouts during
	 * component teardown.
	 *
	 * @type {Set<ReturnType<typeof setTimeout>>}
	 * @memberof RuleListItemComponent
	 */
	private readonly pendingTimeoutHandles:
		Set<ReturnType<typeof setTimeout>> =
			new Set<ReturnType<typeof setTimeout>>();

	/**
	 * Gets or sets the subscription used to listen for violation key updates
	 * and refresh the array item label when the repeater title changes.
	 *
	 * @type {Subscription}
	 * @memberof RuleListItemComponent
	 */
	private violationLabelSubscription: Subscription;

	/**
	 * Handles the on initialization event.
	 * This will load the data needed to display a rule list item, including
	 * rule definitions and presentation definitions. Sets up subscriptions for
	 * reactive array item label updates.
	 *
	 * @async
	 * @memberof RuleListItemComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		const entityInstanceRuleViolation: EntityInstanceRuleViolation =
			new EntityInstanceRuleViolation(this.context.data);

		this.ruleDefinition =
			entityInstanceRuleViolation.ruleDefinition;

		this.ruleViolationWorkflowActions =
			entityInstanceRuleViolation
				.ruleDefinition
				.ruleViolationWorkflowActionDefinitions;

		this.rulePresentationDefinitions =
			await this.ruleService
				.getUniqueViolationPresentationDefinitions(
					entityInstanceRuleViolation);

		this.overrideAllowed = this.getOverrideAllowed();
		this.loadingTables = false;

		afterNextRender(
			() =>
			{
				this.arrayItemLabel =
					this.getArrayItemLabel();
				this.changeDetectorRef.detectChanges();
			},
			{ injector: this.injector });

		this.violationLabelSubscription =
			this.navigationViolationService.violationKeysUpdated
				.subscribe(
					() =>
					{
						const updatedLabel: string =
							this.getArrayItemLabel();

						if (updatedLabel !== this.arrayItemLabel)
						{
							this.arrayItemLabel = updatedLabel;
							this.changeDetectorRef.detectChanges();
						}
					});
	}

	/**
	 * Handles the on destroy event.
	 * This will clean up pending timeout callbacks and unsubscribe from
	 * observables to prevent delayed DOM operations and memory leaks
	 * after the component has been torn down.
	 *
	 * @memberof RuleListItemComponent
	 */
	public ngOnDestroy(): void
	{
		this.pendingTimeoutHandles
			.forEach(
				(timeoutHandle: ReturnType<typeof setTimeout>) =>
				{
					clearTimeout(timeoutHandle);
				});

		this.pendingTimeoutHandles.clear();
		this.violationLabelSubscription?.unsubscribe();
	}

	/**
	 * This method will navigate to and highlight the control displayed in the
	 * list of presentation definitions. This will always navigate to the
	 * control that is a descendant of the current rule violations resource
	 * identifier to allow for repeater based lookups.
	 * @note If the control is not located due to permissions or removed items,
	 * this will display a banner to the user that this control can not be
	 * found.
	 *
	 * @param {IRulePresentationDefinition} selectedItem
	 * The rule presentation definition row display that sent this navigate to
	 * control event.
	 * @memberof RuleListItemComponent
	 */
	public navigateToControl(
		selectedItem: IRulePresentationDefinition): void
	{
		const matchingItem: Element =
			this.findMatchingControlElement(selectedItem);

		if (AnyHelper.isNull(matchingItem))
		{
			EventHelper.dispatchBannerEvent(
				'Control not displayed',
				'The current layout does not hold this control.',
				AppConstants.activityStatus.info);

			return;
		}

		this.openTab(matchingItem);

		this.expandContainers(matchingItem);

		if (matchingItem.classList.contains(
			AppConstants.cssClasses.sectionTitle))
		{
			this.displayElement(matchingItem);

			return;
		}

		this.displayWithAccordionHighlight(
			this.findFieldWrapperElement(matchingItem),
			this.findFocusableElement(matchingItem));
	}

	/**
	 * Finds the form field element matching the sent presentation definition's
	 * data key within the violation's resource identifier scope.
	 *
	 * When the presentation definition's {@link IRulePresentationDefinition}
	 * jsonData contains a {@code filterItemType} property (e.g.
	 * {@code { "filterItemType": "SecondaryResidence" }}), the method first
	 * tries to locate a {@code custom-repeater} element whose
	 * {@code data-repeater-filter} attribute contains that type string,
	 * resolving to the correct typed-asset repeater instead of the first
	 * repeater in layout order. Falls back to the standard data-key lookup
	 * when no filter-type match is found.
	 *
	 * @param {IRulePresentationDefinition} selectedItem
	 * The rule presentation definition used to locate the field.
	 * @returns {Element | null}
	 * The matching field element, or null if not found in the current layout.
	 * @memberof RuleListItemComponent
	 */
	public findMatchingControlElement(
		selectedItem: IRulePresentationDefinition): Element | null
	{
		let matchingItem: Element | null = null;
		const matchingResourceIdentifiers: NodeListOf<Element> =
			document.querySelectorAll(
				`${FormlyConstants.elements.label}[${
					FormlyConstants.attributeKeys
						.dataResourceIdentifier}=
					"${this.context.data.resourceIdentifier}"]`);

		const repeaterType: string | null =
			this.getFilterItemType(selectedItem);

		for (let index = 0;
			index < matchingResourceIdentifiers.length
				&& AnyHelper.isNull(matchingItem);
			index++)
		{
			const matchingResourceIdentifier: any =
				matchingResourceIdentifiers[index]
					?.parentElement // div
					.parentElement // custom control
					.parentElement // formly field
					.parentElement; // Base level or repeater level

			const splitKey: string[] =
				selectedItem.dataKey.split(AppConstants.characters.period);

			if (!AnyHelper.isNullOrWhitespace(
				repeaterType))
			{
				const repeater: Element | null =
					document.querySelector(
						`${FormlyConstants.customControls.customRepeater}`
						+ `[${FormlyConstants.attributeKeys.dataRepeaterType}`
						+ `~="${repeaterType}"]`);

				matchingItem =
					repeater?.querySelector(
						`[data-key="${selectedItem.dataKey}"]`)
						?? repeater;
			}

			matchingItem ??=
				matchingResourceIdentifier?.querySelector(
					`[data-key="${selectedItem.dataKey}"]`)
					?? matchingResourceIdentifier?.querySelector(
						`[data-key="${splitKey[splitKey.length - 1]}"]`);
		}

		return matchingItem;
	}

	/**
	 * Parses the {@code filterItemType} value from a rule presentation
	 * definition's {@code jsonData} string. This value is an optional
	 * field that identifies which repeater a policy-level array rule
	 * targets (e.g. {@code "SecondaryResidence"}).
	 *
	 * @param {IRulePresentationDefinition} selectedItem
	 * The rule presentation definition to read the jsonData from.
	 * @returns {string | null}
	 * The filterItemType string, or null when absent or unparseable.
	 * @memberof RuleListItemComponent
	 */
	public getFilterItemType(
		selectedItem: IRulePresentationDefinition): string | null
	{
		if (AnyHelper.isNullOrWhitespace(
			selectedItem?.jsonData))
		{
			return null;
		}

		try
		{
			const parsedData: any =
				JSON.parse(selectedItem.jsonData);

			return parsedData?.data?.filterItemType ?? null;
		}
		catch
		{
			return null;
		}
	}

	/**
	 * Walks up from a matched field element to find the nearest ancestor
	 * that carries the custom field wrapper class. Handles non-standard
	 * controls that sit deeper in the formly tree.
	 *
	 * @param {Element} startElement
	 * The matched field element to start walking up from.
	 * @returns {Element}
	 * The closest ancestor with the custom-field-wrapper class.
	 * @memberof RuleListItemComponent
	 */
	public findFieldWrapperElement(
		startElement: Element): Element
	{
		let wrapperElement: Element =
			startElement
				.parentElement
				.parentElement
				.parentElement;

		let currentAttempt: number = 1;
		const maximumAttempts: number = 5;

		while (!wrapperElement.classList.contains(
			FormlyConstants.customControls.customFieldWrapper)
				&& currentAttempt <= maximumAttempts)
		{
			wrapperElement = wrapperElement.parentElement;
			currentAttempt++;
		}

		return wrapperElement;
	}

	/**
	 * Returns the focusable element within a matched field. Uses the nested
	 * input when available; falls back to the field element itself.
	 *
	 * @param {any} matchingItem
	 * The matched field element.
	 * @returns {any}
	 * The nested input element, or the matching item if no input is present.
	 * @memberof RuleListItemComponent
	 */
	public findFocusableElement(
		matchingItem: any): any
	{
		return matchingItem.querySelector(
			FormlyConstants.customControls.input)
			?? matchingItem;
	}

	/**
	 * Defers to the next animation frame so that any PrimeNG accordion
	 * expand animations triggered by {@link expandContainers} have been
	 * started by Angular's change detection. Collects all running animations
	 * on ancestor `.p-toggleable-content` elements using the native Web
	 * Animations API ({@link Element.getAnimations}), then waits for every
	 * animation to settle before scrolling to and highlighting the target
	 * field. When no animation is active, the highlight is applied
	 * immediately.
	 *
	 * @param {Element} wrapperElement
	 * The custom-field-wrapper element to scroll to and highlight.
	 * @param {any} focusElement
	 * The element to focus and apply the field border highlight to.
	 * @memberof RuleListItemComponent
	 */
	public displayWithAccordionHighlight(
		wrapperElement: Element,
		focusElement: any): void
	{
		requestAnimationFrame(
			() =>
			{
				const animations: Animation[] =
					this.collectAncestorExpandAnimations(
						wrapperElement);

				if (animations.length === 0)
				{
					this.applyNavigationHighlight(
						wrapperElement,
						focusElement);

					return;
				}

				Promise.allSettled(
					animations.map(
						(animation: Animation) => animation.finished))
					.then(
						() =>
						{
							this.applyNavigationHighlight(
								wrapperElement,
								focusElement);
						});
			});
	}

	/**
	 * Scrolls to and applies red border highlight classes to the wrapper
	 * element and all ancestor accordion tabs. Schedules removal of all
	 * classes using a raw setTimeout so the operation survives any component
	 * teardown triggered by accordion expansion.
	 *
	 * @param {Element} wrapperElement
	 * The element to scroll to and apply the container border highlight to.
	 * @param {any} focusElement
	 * The element to focus and apply the field border highlight to.
	 * @memberof RuleListItemComponent
	 */
	public applyNavigationHighlight(
		wrapperElement: Element,
		focusElement: any): void
	{
		if (!wrapperElement.isConnected)
		{
			return;
		}

		const borderClass: string =
			AppConstants.cssClasses.selectedErrorContainerBorder;
		const fieldClass: string =
			AppConstants.cssClasses.selectedErrorFieldBorder;

		wrapperElement.scrollIntoView(
			{
				block: 'center',
				inline: 'center'
			});

		wrapperElement.classList.add(borderClass);

		if (!AnyHelper.isNull(focusElement))
		{
			focusElement.classList.add(fieldClass);
			focusElement.focus();
		}

		const highlightedTabs: Element[] =
			this.collectAncestorAccordionTabs(wrapperElement);

		highlightedTabs.forEach(
			(tab: Element) =>
			{
				tab.classList.add(borderClass);
			});

		setTimeout(
			() =>
			{
				wrapperElement.classList.remove(borderClass);

				if (!AnyHelper.isNull(focusElement))
				{
					focusElement.classList.remove(fieldClass);
				}

				highlightedTabs.forEach(
					(tab: Element) =>
					{
						tab.classList.remove(borderClass);
					});
			},
			AppConstants.time.twoSeconds
				- AppConstants.time.quarterSecond);
	}

	/**
	 * Walks up the DOM from the given element and collects all ancestor
	 * accordion tab elements in order from innermost to outermost.
	 *
	 * @param {Element} startElement
	 * The element to start walking up from.
	 * @returns {Element[]}
	 * The ancestor accordion tab elements, innermost first.
	 * @memberof RuleListItemComponent
	 */
	public collectAncestorAccordionTabs(
		startElement: Element): Element[]
	{
		const tabs: Element[] = [];

		let current: Element =
			startElement.closest(
				`.${AppConstants.cssClasses.pAccordionContent}`)
				?? null;

		while (!AnyHelper.isNull(current))
		{
			const tab: Element =
				current.closest(
					`.${AppConstants.cssClasses.pAccordionTab}`);

			if (AnyHelper.isNull(tab))
			{
				current = null;
			}
			else
			{
				tabs.push(tab);

				current =
					tab.parentElement?.closest(
						`.${AppConstants.cssClasses.pAccordionContent}`)
						?? null;
			}
		}

		return tabs;
	}

	/**
	 * This method will set the view mode of the rule component to the override
	 * view.
	 *
	 * @memberof RuleListItemComponent
	 */
	public displayOverride(): void
	{
		const source: RuleListComponent =
			<RuleListComponent>this.context.source;

		source.changeSelectedItem.emit(
			this.context.data);
		source.changeDisplayMode.emit(
			AppConstants.displayMode.view);
	}

	/**
	 * This method will open the tab that this element to display is contained
	 * in. Handles both outer page-level tabs (`custom-tab-content`) and nested
	 * tabs inside repeater accordion items (`custom-nested-tab-content`). For
	 * nested tabs the title search is scoped to the nearest accordion item to
	 * avoid false matches in sibling repeater items.
	 *
	 * @param {any} elementToDisplay
	 * The element to be displayed.
	 * @memberof RuleListItemComponent
	 */
	public openTab(
		elementToDisplay: any): void
	{
		const tabKeyIdentifier: string =
			FormlyConstants.attributeKeys.tabKey;
		const parentTabSection: HTMLDivElement =
			elementToDisplay.parentElement?.closest(
				`.${FormlyConstants.customControls.customTabContent}`);

		if (!AnyHelper.isNull(parentTabSection))
		{
			this.clickTabTitleInScope(
				parentTabSection.attributes[tabKeyIdentifier]?.nodeValue,
				document);
		}

		const parentNestedTabSection: HTMLDivElement =
			elementToDisplay.parentElement?.closest(
				`.${FormlyConstants.customControls.customNestedTabContent}`);

		if (AnyHelper.isNull(parentNestedTabSection))
		{
			return;
		}

		this.clickTabTitleInScope(
			parentNestedTabSection.attributes[tabKeyIdentifier]?.nodeValue,
			parentNestedTabSection.closest(
				`.${AppConstants.cssClasses.pAccordionTab}`)
			?? document.documentElement);
	}

	/**
	 * Given a sent element to display, this method will open all of the
	 * associated collapsed views in order to display a nested control in the
	 * layout.
	 *
	 * @param {any} elementToDisplay
	 * The element that will have all parent containers expanded.
	 * @memberof RuleListItemComponent
	 */
	public expandContainers(
		elementToDisplay: any): void
	{
		let matchFound: boolean = true;
		let currentElement = elementToDisplay;

		while (matchFound === true)
		{
			const parentAccordion: any =
				currentElement.parentElement
					.closest(
						`.${AppConstants.cssClasses.pAccordionTab}`);

			matchFound = !AnyHelper.isNull(parentAccordion);

			if (matchFound === true
				&& !parentAccordion.classList.contains(
					AppConstants.cssClasses.pAccordionTabActive))
			{
				parentAccordion.querySelector(
					`.${AppConstants.cssClasses.pAccordionHeaderLink}`)
					.click();
			}

			currentElement = parentAccordion;
		}
	}

	/**
	 * This method will navigate to and highlight the border element. This will
	 * also add a border highlight display to draw attention to the control
	 * and focus if a focusable element is sent.
	 *
	 * @param {Element} borderDisplayElement
	 * The element that will be scrolled to and will display a highlight
	 * error border.
	 * @param {any} focusElement
	 * The element to focus on if available in this controls API.
	 * @memberof RuleListItemComponent
	 */
	public displayElement(
		borderDisplayElement: Element,
		focusElement: any = null): void
	{
		requestAnimationFrame(
			() =>
			{
				const animations: Animation[] =
					this.collectAncestorExpandAnimations(
						borderDisplayElement);

				if (animations.length === 0)
				{
					this.scrollAndHighlight(
						borderDisplayElement,
						focusElement);

					return;
				}

				Promise.allSettled(
					animations.map(
						(animation: Animation) => animation.finished))
					.then(
						() =>
						{
							this.scrollAndHighlight(
								borderDisplayElement,
								focusElement);
						});
			});
	}

	/**
	 * This method will scroll the border element into the center of the
	 * view and apply the flash highlight. This is called once any ancestor
	 * expand animations have settled so the element is in its final
	 * position before scrolling.
	 *
	 * @param {Element} borderDisplayElement
	 * The element that will be scrolled to and will display a highlight
	 * error border.
	 * @param {any} focusElement
	 * The element to focus on if available in this controls API.
	 * @memberof RuleListItemComponent
	 */
	private scrollAndHighlight(
		borderDisplayElement: Element,
		focusElement: any = null): void
	{
		borderDisplayElement.scrollIntoView(
			{
				block: 'center',
				inline: 'center'
			});

		this.applyFlashHighlight(
			borderDisplayElement,
			focusElement);
	}

	/**
	 * Resolves the repeater item label from the violation's resource
	 * identifier by querying the DOM for the matching accordion header.
	 *
	 * @returns {string}
	 * The accordion header text for the repeater item, or empty string
	 * if not inside a repeater.
	 * @memberof RuleListItemComponent
	 */
	public getArrayItemLabel(): string
	{
		const resourceIdentifier: string =
			this.context.data.resourceIdentifier;

		if (AnyHelper.isNullOrEmpty(resourceIdentifier))
		{
			return AppConstants.empty;
		}

		const matchingLabel: Element =
			document.querySelector(
				`${FormlyConstants.elements.label}[${
					FormlyConstants.attributeKeys
						.dataResourceIdentifier}="${resourceIdentifier}"]`);

		if (AnyHelper.isNull(matchingLabel))
		{
			return AppConstants.empty;
		}

		const accordionTab: Element =
			matchingLabel.closest(
				`.${AppConstants.cssClasses.pAccordionTab}`);

		if (AnyHelper.isNull(accordionTab))
		{
			return AppConstants.empty;
		}

		const headerText: Element =
			accordionTab.querySelector(
				`.${AppConstants.cssClasses.pAccordionHeaderText}`);

		return headerText?.textContent?.trim()
			?? AppConstants.empty;
	}

	/**
	 * Creates and returns a user-friendly display name for a workflow action
	 * by adding spaces before capital letters.
	 *
	 * @param {IRuleViolationWorkflowActionDefinition}
	 * ruleViolationWorkflowAction
	 * The workflow action definition to format.
	 * @returns {string}
	 * The formatted display name with spaces before capitals.
	 * @memberof RuleListItemComponent
	 */
	public getActionDisplayName(
		ruleViolationWorkflowAction:
			IRuleViolationWorkflowActionDefinition): string
	{
		const name: string =
			ruleViolationWorkflowAction
				.workflowActionDefinition?.name
				?? AppConstants.empty;

		return StringHelper.beforeCapitalSpaces(name);
	}

	/**
	 * Creates and returns an aria-label value for the quick access
	 * jump-to-field action.
	 *
	 * @param {IRulePresentationDefinition} rulePresentationDefinition
	 * The rule presentation definition associated with the action.
	 * @returns {string}
	 * The aria-label value for the quick access action button.
	 * @memberof RuleListItemComponent
	 */
	public getQuickAccessAriaLabel(
		rulePresentationDefinition:
			IRulePresentationDefinition): string
	{
		const formattedFieldLabel: string =
			this.getFormattedDataKeyLabel(
				rulePresentationDefinition);

		if (AnyHelper.isNullOrWhitespace(formattedFieldLabel))
		{
			return 'Jump to field';
		}

		return `Jump to field ${formattedFieldLabel}`;
	}

	/**
	 * Creates and returns a formatted data key value to display in the
	 * available areas and fields table.
	 *
	 * @param {IRulePresentationDefinition} rulePresentationDefinition
	 * The rule presentation definition that will be parsed for a formatted
	 * data key.
	 * @returns {string}
	 * The markdown ready formatted data key value for display in the area
	 * and fields table.
	 * @memberof RuleListItemComponent
	 */
	public getFormattedDataKey(
		rulePresentationDefinition: IRulePresentationDefinition): string
	{
		return this.getFormattedDataKeyParts(
			rulePresentationDefinition.dataKey)
			.join(
				'<i class="fa fa-fw fa-caret-right nested-field-icon " '
				+ 'aria-hidden="true"></i>');
	}

	/**
	 * Creates and returns the label for the final segment of a presentation
	 * data key, used as the visible text in the Quick Access row.
	 *
	 * @param {IRulePresentationDefinition} rulePresentationDefinition
	 * The rule presentation definition to read the data key from.
	 * @returns {string}
	 * The formatted label for the last data key segment.
	 * @memberof RuleListItemComponent
	 */
	public getFormattedDataKeyLabel(
		rulePresentationDefinition: IRulePresentationDefinition): string
	{
		const parts: string[] =
			this.getFormattedDataKeyParts(
				rulePresentationDefinition?.dataKey
					?? AppConstants.empty);

		return parts.at(-1)
			?? AppConstants.empty;
	}

	/**
	 * Splits and formats a presentation data key for display purposes.
	 *
	 * @param {string} dataKey
	 * The data key value to parse and format.
	 * @returns {string[]}
	 * The formatted key segments.
	 * @memberof RuleListItemComponent
	 */
	private getFormattedDataKeyParts(
		dataKey: string): string[]
	{
		return (dataKey
			?? AppConstants.empty)
			.replace(
				AppConstants.nestedDataIdentifier,
				AppConstants.empty)
			.split(
				AppConstants.characters.period)
			.map(
				(dataKeyPart: string) =>
					StringHelper.beforeCapitalSpaces(
						StringHelper.toProperCase(
							dataKeyPart)));
	}

	/**
	 * Applies a flash highlight effect using JavaScript class cycling.
	 * The border is toggled on → off → on → off to create a visible flash.
	 *
	 * @param {Element} borderDisplayElement
	 * The element to apply the flash border to.
	 * @param {any} focusElement
	 * The element to focus and apply field border to, if available.
	 * @memberof RuleListItemComponent
	 */
	private applyFlashHighlight(
		borderDisplayElement: Element,
		focusElement: any = null): void
	{
		const borderClass: string =
			AppConstants.cssClasses
				.selectedErrorContainerBorder;
		const fieldClass: string =
			AppConstants.cssClasses
				.selectedErrorFieldBorder;

		borderDisplayElement.classList.add(
			borderClass);

		if (!AnyHelper.isNull(focusElement))
		{
			focusElement.classList.add(fieldClass);
			focusElement.focus();
		}

		this.scheduleTimeout(
			() =>
			{
				borderDisplayElement.classList.remove(
					borderClass);

				if (!AnyHelper.isNull(focusElement))
				{
					focusElement.classList.remove(
						fieldClass);
				}
			},
			AppConstants.time.twoSeconds
				- AppConstants.time.quarterSecond);
	}

	/**
	 * Queries the given scope for a `span.p-tabview-title` whose text matches
	 * the sent tab key and clicks its parent anchor to activate the tab.
	 *
	 * @param {string} tabKey
	 * The tab label text to match.
	 * @param {ParentNode} scope
	 * The DOM node to search within.
	 * @memberof RuleListItemComponent
	 */
	private clickTabTitleInScope(
		tabKey: string,
		scope: ParentNode): void
	{
		let tabElement: HTMLAnchorElement = null;

		scope
			.querySelectorAll(
				`span.${AppConstants.cssClasses.tabViewTitle}`)
			.forEach(
				(element: Element) =>
				{
					if (element.textContent.trim() === tabKey)
					{
						tabElement =
							<HTMLAnchorElement>element.parentElement;
					}
				});

		if (!AnyHelper.isNull(tabElement))
		{
			tabElement.click();
		}
	}

	/**
	 * Walks up the DOM from the given element and collects all running
	 * animations on ancestor accordion `.p-toggleable-content` elements.
	 * Delegates ancestor discovery to {@link collectAncestorAccordionTabs}
	 * so that only accordion expansions are matched, not PrimeNG fieldset,
	 * panel, or panel-menu toggleable content.
	 *
	 * @param {Element} startElement
	 * The element to start walking up from.
	 * @returns {Animation[]}
	 * All running animations from ancestor accordion toggleable-content
	 * elements.
	 * @memberof RuleListItemComponent
	 */
	private collectAncestorExpandAnimations(
		startElement: Element): Animation[]
	{
		const animations: Animation[] = [];

		this.collectAncestorAccordionTabs(startElement)
			.forEach(
				(tab: Element) =>
				{
					const toggleableContent: Element =
						tab.querySelector(
							`.${AppConstants.cssClasses.pToggleableContent}`);

					if (!AnyHelper.isNull(toggleableContent))
					{
						animations.push(
							...toggleableContent.getAnimations());
					}
				});

		return animations;
	}

	/**
	 * Schedules a delayed callback and tracks its timeout handle so it can be
	 * canceled during component teardown.
	 *
	 * @param {() => void} callback
	 * The action to execute when the delay elapses.
	 * @param {number} delay
	 * The delay duration in milliseconds.
	 * @memberof RuleListItemComponent
	 */
	private scheduleTimeout(
		callback: () => void,
		delay: number): void
	{
		const timeoutHandle: ReturnType<typeof setTimeout> =
			setTimeout(
				() =>
				{
					this.pendingTimeoutHandles.delete(timeoutHandle);
					callback();
				},
				delay);

		this.pendingTimeoutHandles.add(timeoutHandle);
	}
}